<!--
  Standalone "create a new Field Lens request" wizard.

  Extracted from the create-only path of the `showUserDialog` dialog in
  FieldLens.vue (step 1: pick Account/Type/Subtype, step 2: request details) so
  it can be reused both by FieldLens.vue's own "+ New Request" button and by the
  chat's /request slash command. Deliberately does NOT support edit mode or the
  admin dialog — FieldLens.vue keeps its own inline dialog for those, unchanged.

  This component only resolves and validates the form; it does not call the
  create API itself. It emits `submit` with the fully-resolved payload and lets
  the caller decide what to do with it — FieldLens.vue's usage actually creates
  the request (mirrors the old saveFromDialog() create branch), while the chat's
  usage turns it into an instruction for the model to call create_fieldlens_request,
  since /boost and every other slash command in this feature keep the model in
  the loop rather than bypassing it.
-->
<template>
  <ChatDialogShell v-model="isOpen" :inline="inline" size="xlarge" icon="heroicons:rocket-launch" closable
    @close="emit('close')">
    <template #header>
      <Stack gap="xxsmall">
        <Heading :level="3">Create New Field Lens Request</Heading>
        <Text v-if="step === 1" color="light" size="small">Don't see your request in the table? Submit a new one
          here.</Text>
      </Stack>
    </template>

    <!-- Step 1: Account / Type / Subtype -->
    <Stack v-if="step === 1" gap="small">
      <AutoComplete v-model="caseForm.account" :options="selectableAccounts" showClear optionLabel="name"
        completeOnFocus :apiSearch="searchAccountsByKeyword" :loading="accountsLoading"
        :placeholder="accountsLoading ? 'Loading your accounts...' : 'Select One or Search Accounts...'" />
      <Box justifyContent="between" noWrap gapX="small">
        <Select v-model="caseForm.requestTypeId" :options="typeOptions" optionLabel="label" optionValue="value"
          placeholder="Type" class="w-full" />
        <Select :disabled="!caseForm.requestTypeId" v-model="caseForm.requestSubtypeId"
          :options="subtypeOptionsForSelectedType" optionLabel="label" optionValue="value" placeholder="Subtype"
          class="w-full" />
      </Box>

      <!-- Users who can't create the selected type here are routed to an SE Support Case instead -->
      <Stack alignItems="center" marginTop="small" v-if="showCreateSupportCaseButton">
        <Button color="purple" @click="startSupportCase" :disabled="!newRequestDialogValid">
          Create SE Support Case
        </Button>
      </Stack>
      <Stack alignItems="center" marginTop="small" v-else-if="showContinueButton">
        <Button color="purple" @click="continueToStep2()" :disabled="!newRequestDialogValid" :loading="relatedLoading">
          Continue
        </Button>
      </Stack>
    </Stack>

    <!-- Step 2: Request details -->
    <Stack v-else gap="medium">
      <Stack gap="xxsmall" marginBottom="xsmall">
        <Box v-if="dialogAccount" alignItems="center" noWrap class="w-120" gap="xsmall">
          <Text class="truncate">{{ dialogAccount.name }}</Text>
          <SalesforceIcon class="size-5 self-center" :objectId="dialogAccount.id" />
        </Box>
        <Box full alignItems="center" gap="xsmall">
          <Text color="light" size="small">{{ form.typeName }}</Text>
          <Text color="light" size="small">{{ '—' }}</Text>
          <Text color="light" size="small">{{ form.subtypeName }}</Text>
        </Box>
      </Stack>

      <Stack full gap="xsmall" marginBottom="xsmall">
        <Text size="small" color="light">
          Optionally attach a Support Case or Opportunity related to your chosen Account
        </Text>
        <Box noWrap>
          <Select v-model="selectedSearchType" :options="searchTypeOptions" optionLabel="label" :optionValue="null"
            class="mr-3 self-center" size="large">
            <template #options="{ option }">
              <Box alignItems="center" justifyContent="center">
                <Icon :icon="option.icon" class="size-5" :class="option.color" />
              </Box>
            </template>
            <template #input="{ value }">
              <Box alignItems="center">
                <Box v-if="value">
                  <Icon :icon="value.icon" class="size-5" :class="value.color" />
                </Box>
                <Box v-else>
                  <Text size="small">Search Type</Text>
                </Box>
              </Box>
            </template>
          </Select>

          <AutoComplete v-if="selectedSearchType?.label === 'Opportunity'" v-model="dialogOpp"
            :options="related.opportunities" optionLabel="name" :delay="500" completeOnFocus showClear class="w-full"
            placeholder="Select an Opportunity" />

          <AutoComplete v-if="selectedSearchType?.label === 'SE Support Case'" v-model="dialogCase"
            :options="related.supportCases" optionLabel="subject" :delay="500" completeOnFocus showClear
            class="w-full" placeholder="Select a Support Case" />
        </Box>
        <Box v-if="selectedSearchType?.label === 'Opportunity' && related.opportunitiesError"
          gapX="xxsmall" noWrap class="opacity-80">
          <Icon icon="heroicons:exclamation-triangle" class="size-3.5 mt-px text-amber-600 dark:text-amber-300" />
          <Text size="xsmall" color="amber">
            Couldn't load opportunities, possibly due to Salesforce permissions. You can still submit without attaching one.
          </Text>
        </Box>
        <Box v-if="selectedSearchType?.label === 'SE Support Case' && related.supportCasesError"
          gapX="xxsmall" noWrap class="opacity-80">
          <Icon icon="heroicons:exclamation-triangle" class="size-3.5 mt-px text-amber-600 dark:text-amber-300" />
          <Text size="xsmall" color="amber">
            Couldn't load support cases, possibly due to Salesforce permissions. You can still submit without attaching one.
          </Text>
        </Box>
      </Stack>

      <Stack v-if="!isISVType" gap="xxsmall">
        <InputText id="fieldLensWizardTitle" v-model="form.title" maxlength="100" class="w-full font-attention"
          label="Title" variant="on" :error="isDuplicateTitle" />
        <Box v-if="isDuplicateTitle" alignItems="center" gap="xxsmall">
          <Icon icon="heroicons:exclamation-circle" class="size-4 text-red-600 dark:text-red-400" />
          <Text size="small" color="red">A request with this title already exists.</Text>
        </Box>
      </Stack>

      <!-- ISV (Third-Party App Validation) specific fields -->
      <Stack v-if="isISVType" full gap="medium">
        <Stack full gap="xsmall">
          <Text size="small" color="light">ISV DETAILS</Text>
          <Box noWrap gapX="small" marginTop="xxsmall">
            <InputText id="fieldLensWizardIsvProductName" v-model="isvProductName" label="Product Name" variant="on"
              class="w-2/3" />
            <InputText id="fieldLensWizardIsvProductVersion" v-model="isvProductVersion" label="Version" variant="on"
              class="w-1/3" />
          </Box>
          <Box v-if="isDuplicateTitle" alignItems="center" gap="xxsmall">
            <Icon icon="heroicons:exclamation-circle" class="size-4 text-red-600 dark:text-red-400" />
            <Text size="small" color="red">A request with this product name and version already exists.</Text>
          </Box>
        </Stack>

        <InputText id="fieldLensWizardIsvPartnerName" v-model="form.relatedEntity" label="Partner Name" variant="on"
          class="w-full" />

        <Stack full gap="xsmall" paddingTop="xsmall">
          <Box alignItems="center" gapX="xxsmall">
            <Text size="small" color="light">ISV CONTACT</Text>
            <Text size="small" color="light" class="opacity-70">(optional)</Text>
            <Icon icon="heroicons:information-circle" class="size-4 text-charcoal-500 dark:text-charcoal-500"
              v-tippy="{ content: 'ISV partner contact information. All fields are optional, but providing more detail increases the likelihood of a successful request.' }" />
          </Box>
          <Box noWrap gapX="small" marginTop="xxsmall">
            <InputText id="fieldLensWizardIsvContactName" v-model="isvContactName" label="Name" variant="on"
              class="w-full" />
            <InputText id="fieldLensWizardIsvContactEmail" v-model="isvContactEmail" label="Email" variant="on"
              class="w-full" />
          </Box>
          <Box noWrap gapX="small" marginTop="xsmall">
            <InputText id="fieldLensWizardIsvContactPhone" v-model="isvContactPhone" label="Phone" variant="on"
              class="w-full" />
            <InputText id="fieldLensWizardIsvContactRole" v-model="isvContactRole" label="Role/Title" variant="on"
              class="w-full" />
          </Box>
        </Stack>

        <Stack full gap="xsmall" paddingTop="xsmall" gapY="xxsmall">
          <Box alignItems="center" gapX="xxsmall" marginBottom="xxsmall">
            <Text size="small" color="light">INDUSTRY SEGMENT</Text>
            <Text size="small" color="light" class="opacity-70">(optional)</Text>
            <Icon icon="heroicons:information-circle" class="size-4 text-charcoal-500 dark:text-charcoal-500"
              v-tippy="{ content: 'Pick the industry segment that best fits the requested ISV app or partner. The matching Alliance Manager becomes the Assigned Owner. A best guess is fine; the Alliances team can reassign later.' }" />
          </Box>
          <Select id="fieldLensWizardIndustrySegmentSelector" v-model="selectedIndustrySegment"
            :options="industrySegmentOptions" optionLabel="label" optionValue="value" placeholder="Select one..."
            :loading="assignedOwnerLoading" class="w-130" showClear @change="onIndustrySegmentChange" />
          <Box v-if="assignedOwnerName" alignItems="center" gapX="xxsmall" marginLeft="xxsmall">
            <Icon icon="heroicons:chat-bubble-left-right" class="size-4 text-charcoal-500 dark:text-charcoal-400" />
            <Text size="small" color="light">Assigned owner</Text>
            <Text size="small">{{ assignedOwnerName }}</Text>
          </Box>
          <Text v-if="assignedOwnerError" size="xsmall" color="red">{{ assignedOwnerError }}</Text>
        </Stack>
      </Stack>

      <!-- Product Enhancement (and other non-ISV) fields -->
      <Stack v-else gap="medium">
        <AutoComplete v-model="form.relatedEntity" :options="products" showClear optionLabel="name" completeOnFocus
          placeholder="Select a Nutanix Product" label="Nutanix Product" variant="on" />
        <InputText id="fieldLensWizardJiraUrl" placeholder="(Optional) Jira URL" icon="heroicons:link"
          v-model="form.jiraURL" />
      </Stack>

      <Textarea id="fieldLensWizardDescription" v-model="form.description" label="Description"
        placeholder="Provide a business justification for this request..." variant="in" rows="8" class="mt-2" />

      <Box alignItems="center" gap="small" justifyContent="center">
        <Button :loading="submitting" size="sm" color="purple" @click="submit()"
          :disabled="submitting || !dialogValidForPublishing" v-tippy="{
            onShow: () => !dialogValidForPublishing, placement: 'top',
            content: isDuplicateTitle ? 'A request with this title already exists.' : isISVType ? 'Partner Name, Product Name, Version, and Description are required.' : 'Title and Product are required for publishing.'
          }">
          Publish
        </Button>
        <Button size="sm" :disabled="submitting" @click="emit('close')">
          Cancel
        </Button>
      </Box>
    </Stack>
  </ChatDialogShell>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { subject } from '@casl/ability'
import { uniqBy } from 'lodash'
import { Icon } from '@iconify/vue'
import { v2 } from '@/services/api.js'
import user from '@/services/user.js'
import ChatDialogShell from '@/components/Agent/ChatDialogShell.vue'
import Heading from '@/components/Core/Heading.vue'
import Text from '@/components/Core/Text.vue'
import { Box, Stack } from '@/components/Core/Layout'
import Button from '@/components/Core/Button.vue'
import SalesforceIcon from '@/components/Core/SalesforceIcon.vue'
import InputText from '@/components/Core/Form/InputText.vue'
import Textarea from '@/components/Core/Form/Textarea.vue'
import Select from '@/components/Core/Form/Select.vue'
import AutoComplete from '@/components/Core/Form/AutoComplete.vue'
import useFieldLens from '@/composables/useFieldLens.js'
import useAbility from '@/composables/useAbility.js'
import useCases from '@/composables/useCases.js'

const props = defineProps({
  // Renders as an inline chat card instead of a popup Dialog — see ChatDialogShell.vue.
  inline: {
    type: Boolean,
    default: false,
  },
  // Set by the caller while the create API call (and its initial boost) is in
  // flight. The chat's /request usage never needs this — submit() there just
  // hands off an instruction to the model and closes immediately.
  submitting: {
    type: Boolean,
    default: false,
  },
  // The caller's already-fetched "my accounts" list (FieldLens.vue passes its own),
  // so the picker is populated immediately instead of waiting on a second fetch.
  accounts: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits(['close', 'submit'])

// Always true for the lifetime of this component; existing purely so the Dialog
// (which expects a v-model) can drive its own open/close transition. The caller
// controls actual mounting via v-if.
const isOpen = ref(true)

const store = useStore()
const router = useRouter()
const { ability } = useAbility()
const { updateObjectId } = useCases()

// useFieldLens()'s refs are module-level singletons, so this picks up whatever
// FieldLens.vue (or any other consumer) has already fetched, and any fetch this
// component triggers is visible there too.
const {
  fieldLens,
  typesWithSubtypes,
  products,
  allianceManagerBySegment,
  fetchFieldLens,
  fetchRequestTypes,
  fetchProducts,
  fetchAllianceManagers,
  findAllianceManagerUser,
} = useFieldLens()

// ╔══════════════════════════════════════════════════════════════════════════════╗
// ║ STEP 1 — Account / Type / Subtype                                           ║
// ╚══════════════════════════════════════════════════════════════════════════════╝

const step = ref(1)
const caseForm = ref({ account: null, requestTypeId: null, requestSubtypeId: null })
const myAccounts = ref([])
const accountsLoading = ref(props.accounts.length === 0)
const relatedLoading = ref(false)

// Prefer the caller's list; fall back to our own fetch if none was passed in (like when ai chat calls /request command)
const accountList = computed(() => {
  if (props.accounts.length > 0) {
    return props.accounts
  }
  return myAccounts.value
})

const selectableAccounts = computed(() =>
  accountList.value.map((account) => ({
    ...account,
    trailingIcon: {
      icon: 'heroicons:check-badge',
      color: 'text-green-700 dark:text-green-600',
      tippy: 'My Account',
    },
  }))
)

async function searchAccountsByKeyword(options, query) {
  if (!query) return options

  const accounts = (await v2.get(`/accounts?filter[search]=${query}`)).data

  return uniqBy([...options, ...accounts], 'id')
}

const typeOptions = computed(() =>
  typesWithSubtypes.value.map((type) => ({ label: type.name, value: type.id }))
)

const subtypesByTypeId = computed(() => {
  const map = {}
  for (const type of typesWithSubtypes.value) {
    // Retired subtypes stay on existing requests but can't be picked for new ones
    map[type.id] = type.subtypes
      .filter((subtype) => subtype.active)
      .map((subtype) => ({ label: subtype.name, value: subtype.id }))
  }
  return map
})

const subtypeOptionsForSelectedType = computed(() => {
  if (!caseForm.value.requestTypeId) {
    return []
  }
  return subtypesByTypeId.value[caseForm.value.requestTypeId] || []
})

const selectedTypeRecord = computed(() =>
  typesWithSubtypes.value.find((type) => type.id === caseForm.value.requestTypeId) || null
)
const selectedTypeName = computed(() => selectedTypeRecord.value?.name || null)

/* Theaters taking part in the Product Enhancement pilot. MUST stay in sync with
   middleware/paseto.js and the equivalent gate in FieldLens.vue. */
const PRODUCT_ENHANCEMENT_THEATERS = ['EMEA', 'Worldwide']

/* Mirrors FieldLens.vue's isAdmin: Admins (Alliances/PM users) get Manage on every
   request, which is also who's exempt from the Product Enhancement theater gate. */
const isAdmin = computed(() =>
  ability.value.can('delete', subject('FieldLens', { typeName: 'Third-Party App Validation', deleted: false }))
)

const worldwideOrEMEA = computed(() => {
  const currentUser = store.state.sfUserData || {}
  return PRODUCT_ENHANCEMENT_THEATERS.includes(currentUser.theater) || isAdmin.value
})

const canCreateSelectedType = computed(() => {
  if (selectedTypeName.value === 'Third-Party App Validation') return true
  if (selectedTypeName.value === 'Product Enhancement') return worldwideOrEMEA.value
  return false
})

// Users who can't create the selected type here are routed to an SE Support Case instead
const showCreateSupportCaseButton = computed(() => !worldwideOrEMEA.value && !canCreateSelectedType.value)
const showContinueButton = computed(() => canCreateSelectedType.value)
const newRequestDialogValid = computed(() =>
  Boolean(caseForm.value.account && caseForm.value.requestTypeId && caseForm.value.requestSubtypeId)
)

// Push to the router the user's chosen fields for a new SE Support Case
function startSupportCase() {
  updateObjectId(caseForm.value.account.id)

  router.push({
    name: 'support-case',
    query: {
      parentId: caseForm.value.account.id,
      requestTypeId: caseForm.value.requestTypeId,
      requestSubtypeId: caseForm.value.requestSubtypeId,
    },
  })
}

// ╔══════════════════════════════════════════════════════════════════════════════╗
// ║ STEP 2 — Request Details                                                    ║
// ╚══════════════════════════════════════════════════════════════════════════════╝

const dialogAccount = ref(null)
const dialogOpp = ref(null)
const dialogCase = ref(null)
const related = ref({ opportunities: [], supportCases: [], opportunitiesError: false, supportCasesError: false })

// Attaching an opp/case is optional, and some users lack field perms on those objects,
// so a failed fetch just leaves that dropdown empty (with a note) instead of blocking the form
async function fetchDialogRelated(accountId) {
  const [cases, opps] = await Promise.allSettled([
    v2.get(`/accounts/${accountId}/se-support-cases`),
    v2.get(`/accounts/${accountId}/opportunities`)
  ])

  related.value.opportunitiesError = opps.status === 'rejected'
  related.value.supportCasesError = cases.status === 'rejected'

  if (opps.status === 'fulfilled') {
    related.value.opportunities = (opps.value.data || []).filter((opp) => !opp.techCloseStatus?.includes('Closed'))
  }
  else {
    console.error('Failed to fetch related opportunities', opps.reason)
    related.value.opportunities = []
  }

  if (cases.status === 'fulfilled') {
    related.value.supportCases = cases.value.data || []
  }
  else {
    console.error('Failed to fetch related support cases', cases.reason)
    related.value.supportCases = []
  }
}

async function continueToStep2() {
  relatedLoading.value = true
  try {
    dialogAccount.value = caseForm.value.account

    form.value.typeId = caseForm.value.requestTypeId
    form.value.subtypeId = caseForm.value.requestSubtypeId

    const type = typesWithSubtypes.value.find((type) => type.id === form.value.typeId)
    const subtype = type.subtypes.find((subtype) => subtype.id === form.value.subtypeId) || null

    form.value.typeName = type.name
    form.value.subtypeName = subtype?.name || null

    await fetchDialogRelated(dialogAccount.value.id)

    step.value = 2
  }
  catch (err) {
    console.error(err)
  }
  finally {
    relatedLoading.value = false
  }
}

// This component only ever represents the "create new request" flow, so Account
// is always excluded here (mirrors the showUserDialog === true branch of the
// equivalent list in FieldLens.vue, which also always excludes it in that case).
const searchTypeOptions = ref([
  { label: 'Opportunity', icon: 'heroicons:sparkles', color: 'text-[#FCB95B]' },
  { label: 'SE Support Case', icon: 'heroicons:briefcase', color: 'text-[#6ac312]' },
])
const selectedSearchType = ref(searchTypeOptions.value[0])

const form = ref({
  title: null,
  description: null,
  jiraURL: null,
  relatedEntity: null,
  typeId: null,
  subtypeId: null,
  typeName: null,
  subtypeName: null,
})

// True when the current dialog form is for a Third-Party App Validation (ISV) request
const isISVType = computed(() => form.value.typeName === 'Third-Party App Validation')

const isvProductName = ref(null)
const isvProductVersion = ref(null)
const isvContactName = ref(null)
const isvContactEmail = ref(null)
const isvContactPhone = ref(null)
const isvContactRole = ref(null)

// Keep the Title in sync as "Product Name Version" while filling out an ISV request
watch([isvProductName, isvProductVersion], ([productName, productVersion]) => {
  if (!isISVType.value) return
  form.value.title = [productName, productVersion].filter(Boolean).join(' ') || null
})

function buildDescriptionWithISVContact(baseDescription) {
  const contactLines = []
  if (isvContactName.value) contactLines.push(`Name: ${isvContactName.value}`)
  if (isvContactEmail.value) contactLines.push(`Email: ${isvContactEmail.value}`)
  if (isvContactPhone.value) contactLines.push(`Phone: ${isvContactPhone.value}`)
  if (isvContactRole.value) contactLines.push(`Role/Title: ${isvContactRole.value}`)

  const base = (baseDescription || '').trim()
  if (contactLines.length === 0) return base

  const block = ['ISV CONTACT', ...contactLines].join('\n')
  return base ? `${base}\n\n${block}` : block
}

const industrySegmentOptions = computed(() => {
  const segments = Object.keys(allianceManagerBySegment.value).sort()
  const options = []
  for (const segment of segments) {
    options.push({ label: segment, value: segment })
  }
  return options
})

const selectedIndustrySegment = ref(null)
const assignedOwnerName = computed(() => allianceManagerBySegment.value[selectedIndustrySegment.value])

// The picked segment's Alliance Manager user record, saved as the request's contact (shown as Assigned Owner)
const resolvedContact = ref(null)
const assignedOwnerLoading = ref(false)
const assignedOwnerError = ref(null)

// Set the contact (shown as Assigned Owner) to the segment's Alliance Manager
async function onIndustrySegmentChange() {
  assignedOwnerError.value = null
  resolvedContact.value = null

  const managerName = allianceManagerBySegment.value[selectedIndustrySegment.value]
  if (!managerName) {
    return
  }

  assignedOwnerLoading.value = true
  try {
    const contact = await findAllianceManagerUser(managerName)
    if (contact) {
      resolvedContact.value = contact
    }
    else {
      assignedOwnerError.value = `Could not find a user record for ${managerName}.`
    }
  }
  finally {
    assignedOwnerLoading.value = false
  }
}

// Check if the title in the form already exists on another field lens
const isDuplicateTitle = computed(() => {
  const title = form.value.title
  if (!title) return false

  return fieldLens.value.some((fl) => fl.title?.trim().toLowerCase() === title.trim().toLowerCase())
})

// Users must pick a title and a product; enforce more for ISV requests
const dialogValidForPublishing = computed(() => {
  const { title, relatedEntity, description } = form.value

  if (!title || isDuplicateTitle.value) return false

  if (isISVType.value) {
    return Boolean(relatedEntity && isvProductName.value && isvProductVersion.value && description)
  }

  return relatedEntity !== null
})

function resolveSelectedOpportunityAndSupportCase() {
  if (selectedSearchType.value?.label === 'Opportunity' && dialogOpp.value) {
    return { opportunity: dialogOpp.value, supportCase: null }
  }

  if (selectedSearchType.value?.label === 'SE Support Case' && dialogCase.value) {
    const supportCase = dialogCase.value
    const opportunity = supportCase.opportunity ? { id: supportCase.opportunity } : null
    return { opportunity, supportCase }
  }

  return { opportunity: null, supportCase: null }
}

function submit() {
  if (props.submitting || !dialogValidForPublishing.value) return

  const description = isISVType.value
    ? buildDescriptionWithISVContact(form.value.description)
    : form.value.description

  // ISV requests store relatedEntity as the typed partner name (a plain string);
  // Product Enhancement stores the selected product object, so only its name goes out.
  const relatedEntity = isISVType.value ? form.value.relatedEntity : form.value.relatedEntity?.name

  const { opportunity, supportCase } = resolveSelectedOpportunityAndSupportCase()

  emit('submit', {
    account: dialogAccount.value,
    opportunity,
    supportCase,
    typeId: form.value.typeId,
    subtypeId: form.value.subtypeId,
    typeName: form.value.typeName,
    subtypeName: form.value.subtypeName,
    title: form.value.title,
    // `description` is already folded with the ISV contact block, matching what
    // saveFromDialog() used to send straight to the raw /field-lens endpoint.
    // `rawDescription` is the untouched textarea value — needed by callers (like
    // the chat's /request answer) that hand off to create_fieldlens_request,
    // since that tool does its own ISV contact folding and would double it up
    // if given the already-folded text.
    description,
    rawDescription: form.value.description,
    jiraURL: form.value.jiraURL,
    relatedEntity,
    industrySegment: isISVType.value ? selectedIndustrySegment.value : null,
    contact: isISVType.value ? resolvedContact.value : null,
    // Raw (non-folded) ISV fields, kept alongside `description`/`rawDescription`
    // above so callers that need discrete fields (create_fieldlens_request's
    // product_name/product_version/isv_contact_* params) don't have to re-parse it.
    isISVType: isISVType.value,
    productName: isISVType.value ? isvProductName.value : null,
    partnerName: isISVType.value ? form.value.relatedEntity : null,
    productVersion: isISVType.value ? isvProductVersion.value : null,
    isvContactName: isvContactName.value,
    isvContactEmail: isvContactEmail.value,
    isvContactPhone: isvContactPhone.value,
    isvContactRole: isvContactRole.value,
  })
}

async function fetchMyAccounts() {
  try {
    myAccounts.value = await user.getAccounts({
      offset: 0,
      limit: 50,
      orderBy: 'LastModifiedDate',
      orderDirection: 'DESC',
    })
  }
  catch (err) {
    console.error(err)
  }
  finally {
    accountsLoading.value = false
  }
}

onMounted(async () => {
  if (accountsLoading.value){
    fetchMyAccounts()
   } 

  try {
    await Promise.all([fetchRequestTypes(), fetchProducts(), fetchFieldLens(), fetchAllianceManagers()])
  }
  catch (err) {
    console.error(err)
  }
})
</script>
